import {
  BankAccountInfiniteSelectField,
  DocumentTypesInfiniteSelectField,
  FloatingFormItem,
  InputDate,
  InputDecimal,
  PlanAccountsSelect,
  SaveButton,
} from '@/components/_common';
import CostCenterInfiniteSelectField from '@/components/_common/CostCenterInfiniteSelectField';
import PaymentConditionSelect from '@/components/_common/PaymentConditionSelect';
import { PaymentCondition } from '@/core/domain/enterprise/PaymentCondition/PaymentCondition.types';
import {
  PlanAccountDestination,
  PlanAccountHierarchy,
} from '@/core/domain/enterprise/PlanAccounts/PlanAccounts.types';
import { useAccountReceivableGenerateFinancial } from '@/core/domain/financial/AccountReceivable/AccountReceivable.hooks';
import {
  AccountReceivableFinancialPending,
  GenerateFinancialInstallment,
} from '@/core/domain/financial/AccountReceivable/AccountReceivable.types';
import { useDefaultEmptyFieldError, useDisclosure } from '@/hooks';
import { getTableScrollX } from '@/utils/table';
import {
  Alert,
  Col,
  Form,
  Input,
  message,
  Popover,
  Row,
  Table,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/es/table/interface';
import moment, { Moment } from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import WorkOrderFinancialInstallments from '../WorkOrderFinancialInstallments';
import './style.less';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import formatterPrice from '@/utils/number/formatterPrice';
import StoneOrderCreateModal from '@/components/Stone/StoneOrderCreateModal';
import { getWorkorderComplementNumber } from '@/utils/functions/getWorkorderComplementNumber';
import { CustomerType } from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import { WorkOrderComplementStatus } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import {
  StoneOrderInstallment,
  StoneOrderOriginType,
} from '@/core/domain/stone/orders/orders.types';
import { useStoneAccountDetailbyCompany } from '@/core/domain/stone/stoneAccount/stoneAccount.hooks';
import StoneOrderProcessInfo from '@/components/Stone/StoneOrderProcessInfo';
import { StoneDevice } from '@/core/domain/stone/devices/devices.types';
import StoneDevicesSelectModal from '@/components/Stone/StoneDevicesSelectModal';
import ActionIcon from '@/components/_common/ActionIcon';
import { useDocumentType } from '@/core/domain/enterprise/DocumentTypes/DocumentTypes.hooks';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import { useStoneDeviceList } from '@/core/domain/stone/devices/devices.hooks';
import { useStoneOrderHasOpenMutate } from '@/core/domain/stone/orders/orders.hooks';
import { DocumentTypePaymentType } from '@/core/domain/enterprise/DocumentTypes/DocumentTypes.types';
import TEF, { TefOnResultParams } from '@/components/TEF';
import FinancialTypeGenerate, {
  FinancialType,
} from '@/components/_common/FinancialTypeGenerate';
import { usePaymentTransactionCreate } from '@/core/domain/payment-gateway/transactions/transactions.hooks';
import {
  PaymentFinancialPendingStatus,
  TransactionAccreditor,
  TransactionClientType,
  TransactionEntityType,
  TransactionPaymentTypeByTef,
} from '@/core/domain/payment-gateway/transactions/transactions.types';
import TEFProcessFinancial, {
  TEFProcessFinancialRef,
} from '@/components/TEF/TEFProcessFinancial';

const initialValues = {
  value: 0,
  toNegotiate: 0,
  negotiated: 0,
  discountPercent: 0,
  discountPrice: 0,
  incrisePercent: 0,
  incrisePrice: 0,
  installmentQuantity: 1,
  installmentsInterval: 30,
  dueDate: moment(),
  installments: [],
};

export type WorkOrderFinancialFormValues = {
  value: number;
  toNegotiate: number;
  negotiated: number;
  discountPercent: number;
  discountPrice: number;
  incrisePercent: number;
  incrisePrice: number;
  installmentQuantity: number;
  installmentsInterval: number;
  dueDate: Moment;
  planAccountId: number;
  costCenterId?: number;
  insuerId?: number;
  documentTypeId?: number;
  bankAccountId?: number;
  authorizationNumber?: string;
};

export type WorkorderFinancialInstallment = Partial<
  GenerateFinancialInstallment & {
    baseValue: number;
    installmentBaseValue: number;
  }
>;

type Props = {
  onChange: (value: WorkOrderFinancialFormValues) => void;
  financialPendingData?: AccountReceivableFinancialPending;
  type: 'private' | 'insurer';
  refetch: () => Promise<void>;
};

const WorkOrderFinancialConfig: React.FC<Props> = (props) => {
  const [form] = Form.useForm<WorkOrderFinancialFormValues>();
  const ruleRequired = useDefaultEmptyFieldError();
  const formValues = Form.useWatch([], form);
  const [paymentCondition, setPaymentCondition] = useState<PaymentCondition>();
  const [installments, setInstallments] = useState<
    WorkorderFinancialInstallment[]
  >([]);

  const paymentStatusRef = useRef<TEFProcessFinancialRef>(null);

  const queryChanel = useQueryChanel();

  const {
    workorder: { data: workorder },
    complement: { data: complement },
    company: { companyConfig, data: company },
  } = useWorkorderDetailContext();

  const [financialType, setFinancialType] = useState<FinancialType>(
    FinancialType.Default,
  );

  const documentTypeId = Form.useWatch('documentTypeId', form);
  const valueField = Form.useWatch('value', form);

  const documentTypeDetail = useDocumentType(documentTypeId);

  useEffect(() => {
    if (documentTypeDetail.data) {
      form.setFieldValue(
        'bankAccountId',
        documentTypeDetail.data.bankAccountId,
      );
    }
  }, [documentTypeDetail.dataUpdatedAt]);

  const isStone = financialType === 'stone';

  const [stoneDevice, setStoneDevice] = useState<StoneDevice>();

  const [stoneModalOpened, stoneModalAction] = useDisclosure(false);

  const stoneDevices = useStoneDeviceList({
    companyId: workorder?.company.id,
    isActive: true,
  });

  const stoneHasOrderOpen = useStoneOrderHasOpenMutate();

  useEffect(() => {
    const dataValue =
      props.type === 'private'
        ? props.financialPendingData?.totalPrivateValue || 0
        : props.financialPendingData?.totalInsurerValue || 0;

    if (!!dataValue) {
      form.setFieldsValue({
        value: dataValue,
        negotiated: dataValue,
        discountPrice: props.financialPendingData?.issDiscount || 0,
        discountPercent: props.financialPendingData?.issDiscountPercentage || 0,
      });
    }
  }, [props.financialPendingData]);

  useEffect(() => {
    if (paymentCondition?.installments.length) {
      form.setFieldsValue({
        installmentQuantity: paymentCondition.installments.length,
        installmentsInterval: paymentCondition.installments[0].numberOfDays,
      });
    }
  }, [paymentCondition]);

  const isInstallmentFree = !!companyConfig?.isInstallmentFree;
  const discountIncrise =
    -(formValues?.discountPrice || 0) + (formValues?.incrisePrice || 0);

  const isCanceled = complement?.status === WorkOrderComplementStatus.Cancelled;

  const generateFinancial = useAccountReceivableGenerateFinancial();
  const stoneAccount = useStoneAccountDetailbyCompany(workorder?.company.id);

  const installmentSize = installments.length;

  const hasMax12InstallmentAndLower10 =
    installmentSize <= 12 &&
    installments.some((item) => Number(item.installmentValue || 0) < 0.1);

  const hasOver12InstallmentAndLower10 =
    installmentSize > 12 &&
    installments.some((item) => Number(item.installmentValue || 0) < 10);

  const [tefOpened, tefAction] = useDisclosure();

  function discountPercentChange(
    field: WorkOrderFinancialFormValues,
    fields: WorkOrderFinancialFormValues,
  ) {
    if (typeof field.discountPercent === 'number') {
      form.setFieldValue(
        'discountPrice',
        (field.discountPercent * fields.negotiated) / 100,
      );
    }
  }

  function discountPriceChange(
    field: WorkOrderFinancialFormValues,
    fields: WorkOrderFinancialFormValues,
  ) {
    if (typeof field.discountPrice === 'number') {
      form.setFieldValue(
        'discountPercent',
        (field.discountPrice * 100) / fields.negotiated,
      );
    }
  }

  function incrisePercentChange(
    field: WorkOrderFinancialFormValues,
    fields: WorkOrderFinancialFormValues,
  ) {
    if (typeof field.incrisePercent === 'number') {
      form.setFieldValue(
        'incrisePrice',
        (field.incrisePercent * fields.negotiated) / 100,
      );
    }
  }

  function incrisePriceChange(
    field: WorkOrderFinancialFormValues,
    fields: WorkOrderFinancialFormValues,
  ) {
    if (typeof field.incrisePrice === 'number') {
      form.setFieldValue(
        'incrisePercent',
        (field.incrisePrice * 100) / fields.negotiated,
      );
    }
  }

  function toNegotiate() {
    return formatterPrice(
      (formValues?.value || 0) - (formValues?.negotiated || 0),
    );
  }

  function getTotalValue() {
    return (
      <span style={{ color: '#33ab2f', fontWeight: 'bold' }}>
        {formatterPrice((formValues?.negotiated || 0) + discountIncrise)}
      </span>
    );
  }

  function onValuesChange(
    field: WorkOrderFinancialFormValues,
    fields: WorkOrderFinancialFormValues,
  ) {
    discountPercentChange(field, fields);
    discountPriceChange(field, fields);
    incrisePercentChange(field, fields);
    incrisePriceChange(field, fields);

    form.validateFields();
  }

  async function handleGenerate() {
    try {
      const values = await form.validateFields();

      const orders = await Promise.all(
        stoneDevices.data?.map((device) =>
          stoneHasOrderOpen.mutateAsync(device.id),
        ) || [],
      );

      if (
        orders.some(
          (orderInfo) =>
            orderInfo.hasOpenOrder &&
            orderInfo.originType === StoneOrderOriginType.WORK_ORDER &&
            orderInfo.originId === workorder?.id,
        )
      ) {
        message.warn('Existe um processamento da Stone para este orçamento');

        queryChanel({
          invalidateQueries: [['stoneOrder/hasOpen'], ['stoneDevice/list']],
          refetchQueries: [['stoneOrder/hasOpen'], ['stoneDevice/list']],
        });

        return;
      }

      if (!installments.every((item) => !!item.documentTypeId)) {
        message.warn('Preencha todos os tipos de documentos das parcelas');
        return;
      }

      if (isStone) {
        return stoneModalAction.open();
      }

      if (workorder) {
        const installmentsReceivable = installments.map(
          (item: WorkorderFinancialInstallment) => ({
            discountIncrease: Number(item.discountIncrease),
            documentTypeId: Number(item.documentTypeId),
            dueDate: String(item.dueDate),
            installmentNumber: Number(item.installmentNumber) + 1,
            installmentValue: Number(item.installmentValue),
          }),
        );

        await generateFinancial.mutateAsync({
          workOrderId: workorder.id,
          companyId: workorder.company.id,
          planAccountId: values.planAccountId,
          costCenterId: values?.costCenterId,
          authorizationNumber: values?.authorizationNumber,
          customerId:
            props.type === 'private' ? workorder.customer?.id : undefined,
          insuerId:
            props.type === 'insurer'
              ? complement?.insurer?.id || workorder.insurerId
              : undefined,
          installmentReceivableWorkOrders: installmentsReceivable,
          bankAccountId: values?.bankAccountId,
        });
        form.resetFields();
      }
    } catch {}
  }

  const columns: ColumnsType<WorkOrderFinancialFormValues> = [
    {
      dataIndex: 'value',
      title: 'Valores',
      width: 150,
      render: (value) => (
        <>
          <Form.Item hidden name="value">
            <Input />
          </Form.Item>
          {formatterPrice(value)}
        </>
      ),
    },
    {
      dataIndex: 'toNegotiate',
      title: 'A negociar',
      width: 150,
      render: toNegotiate,
    },
    {
      dataIndex: 'negotiated',
      title: 'Negociado',
      width: 200,
      className: 'form-item',
      render: () => (
        <Form.Item
          name="negotiated"
          style={{ margin: 0 }}
          rules={[
            {
              type: 'number',
              max: formValues?.value,
              message: `Máximo ${formatterPrice(formValues?.value)}`,
            },
          ]}
          getValueFromEvent={(value) => {
            return value || 0;
          }}
        >
          <InputDecimal min={0} />
        </Form.Item>
      ),
    },
    {
      dataIndex: 'discountPercent',
      title: 'Desc.(%)',
      width: 150,
      className: 'form-item',
      render: () => (
        <Form.Item
          name="discountPercent"
          style={{ margin: 0 }}
          rules={[{ type: 'number', max: 100, message: 'Máximo de 100%' }]}
          getValueFromEvent={(value) => {
            return value || 0;
          }}
        >
          <InputDecimal min={0} disabled={!isInstallmentFree} />
        </Form.Item>
      ),
    },
    {
      dataIndex: 'discountPrice',
      title: 'Desc.($)',
      width: 150,
      className: 'form-item',
      render: () => (
        <Form.Item
          name="discountPrice"
          style={{ margin: 0 }}
          rules={[
            {
              type: 'number',
              max: formValues?.negotiated,
              message: `Máximo ${formatterPrice(formValues?.negotiated)}`,
            },
          ]}
          getValueFromEvent={(value) => {
            return value || 0;
          }}
        >
          <InputDecimal min={0} disabled={!isInstallmentFree} />
        </Form.Item>
      ),
    },
    {
      dataIndex: 'incrisePercent',
      title: 'Acres.(%)',
      width: 150,
      className: 'form-item',
      render: () => (
        <Form.Item
          name="incrisePercent"
          style={{ margin: 0 }}
          rules={[
            {
              type: 'number',
              max: 100,
              message: `Máximo de 100%`,
            },
          ]}
          getValueFromEvent={(value) => {
            return value || 0;
          }}
        >
          <InputDecimal min={0} disabled={!isInstallmentFree} />
        </Form.Item>
      ),
    },
    {
      dataIndex: 'incrisePrice',
      title: 'Acres.($)',
      width: 150,
      className: 'form-item',
      render: () => (
        <Form.Item
          name="incrisePrice"
          style={{ margin: 0 }}
          rules={[
            {
              type: 'number',
              max: formValues?.negotiated,
              message: `Máximo ${formatterPrice(formValues?.negotiated)}`,
            },
          ]}
          getValueFromEvent={(value) => {
            return value || 0;
          }}
        >
          <InputDecimal min={0} disabled={!isInstallmentFree} />
        </Form.Item>
      ),
    },
    {
      title: 'Valor total',
      width: 100,
      render: getTotalValue,
      align: 'right',
      fixed: 'right',
    },
  ];

  function getInstallmentOrderStone(): StoneOrderInstallment[] {
    if (!installments.length && !documentTypeDetail.data && !isStone) {
      return [];
    }

    const documentType = documentTypeDetail.data;

    const installmentSize = installments.length;

    const rate = documentType?.rates?.find(
      ({ numberInstallments }) => numberInstallments === installmentSize,
    );

    const rateValue = (installmentValue: number) =>
      +(
        ((documentType?.fixedRate
          ? documentType.percentRate
          : rate?.percentRate || 0) /
          100) *
        (installmentValue || 0)
      ).toFixed(2);

    return installments.map((item) => {
      return {
        documentTypeId: item.documentTypeId as number,
        discountIncrease: item.discountIncrease,
        description: `O.S. ${getWorkorderComplementNumber(
          workorder?.number,
          complement?.number,
        )} - ${
          complement?.customerType === CustomerType.Private
            ? 'PARTICULAR'
            : 'SEGURADORA'
        }`,
        documentNumber: getWorkorderComplementNumber(
          workorder?.number,
          complement?.number,
        ),
        rateValue: rateValue(
          (item.installmentValue || 0) - (item.discountIncrease || 0),
        ),
      };
    });
  }

  const getCustomerId = useMemo(() => {
    if (props.type === 'private') {
      return workorder?.customer?.id;
    }
    return undefined;
  }, [props.type, workorder]);

  const getInsurerId = useMemo(() => {
    if (props.type === 'insurer' && workorder?.insurerId) {
      return workorder?.insurerId;
    }

    return undefined;
  }, [props.type, workorder]);

  const transactionCreate = usePaymentTransactionCreate();

  const handleTefResult = async (result: TefOnResultParams) => {
    if (!workorder) return message.error('Dados do orçamento inválidos.');

    await transactionCreate.mutateAsync({
      terminalNsu: result.resultado?.tef.nsuTerminal,
      documentTypeId: result.documentTypeId,
      installmentQuantity: result.installments || 1,
      planAccountId: result.planAccountId,
      totalAmount: result.amount,
      acquirerCnpj: result.resultado?.tef.cnpjCredenciadora,
      acquirerCode: result.resultado?.tef.codigoAdquirente,
      code: result.resultado?.tef.sequencial,
      uniqueId: result.resultado?.tef.uniqueID,
      authorizationCode: result.resultado?.tef.codigoAutorizacao,
      cardBrandName: result.resultado?.tef?.nomeBandeira?.toLowerCase(),
      accreditor: TransactionAccreditor.TefElgin,
      clientType:
        props.type === 'private'
          ? TransactionClientType.Customer
          : TransactionClientType.Insurer,
      entityType: TransactionEntityType.WorkOrder,
      paymentType: TransactionPaymentTypeByTef[result.funcao],
      companyId: workorder.company.id,
      entityId: workorder.id,
      clientId:
        props.type === 'private' ? workorder.customer?.id : workorder.insurerId,
      entityCode: complement?.complementIdentifier,
    });

    await paymentStatusRef.current?.statusRefetch();

    form.resetFields();
  };

  return (
    <div style={{ position: 'relative' }}>
      <TEFProcessFinancial
        ref={paymentStatusRef}
        enabled={transactionCreate.isSuccess}
        entityId={workorder?.id}
        entityType={TransactionEntityType.WorkOrder}
        refresh={async (status) => {
          if (status === PaymentFinancialPendingStatus.COMPLETED) {
            queryChanel({
              refetchQueries: [
                [
                  ['accountReceivable/financialPending', workorder?.id],
                  [['accountReceivable/financialSummary', workorder?.id]],
                ],
              ],
              resetQueries: [['dashboard-data']],
            });
            await props.refetch();
            transactionCreate.reset();
          }
        }}
      />

      <TEF
        opened={tefOpened}
        onClose={() => {
          setFinancialType(FinancialType.Default);
          tefAction.close();
        }}
        amout={valueField}
        onResult={handleTefResult}
        cnpj={company?.cnpj}
      />

      <Form
        form={form}
        onValuesChange={onValuesChange}
        initialValues={initialValues}
      >
        <Row style={{ marginBottom: 24 }} align="middle" gutter={[24, 24]}>
          <Col span={24}>
            <FinancialTypeGenerate
              tefEnabled={
                complement?.status === WorkOrderComplementStatus.Authorized
              }
              visible={(props.financialPendingData?.totalValuePendent || 0) > 0}
              companyId={company?.id}
              type={financialType}
              onChange={(value) => {
                form.setFields([
                  { name: 'documentTypeId', value: undefined },
                  { name: 'installmentQuantity', value: 1 },
                ]);

                if (value === 'default') {
                  setStoneDevice(undefined);
                } else if (value === FinancialType.Tef) {
                  setStoneDevice(undefined);
                  tefAction.open();
                }

                documentTypeDetail.refetch();
                setFinancialType(value);
              }}
            />
          </Col>

          <Col flex={1}>
            <Typography.Title style={{ margin: 0 }} level={4}>
              Configuração
            </Typography.Title>
          </Col>

          <Col>
            <SaveButton
              onClick={handleGenerate}
              loading={generateFinancial.isLoading}
              disabled={
                !hasPermissions([
                  Authorities.MOVIMENTATION_WORKORDER_GENERATE_FINANCIAL,
                ]) ||
                (isStone && !stoneDevice) ||
                (financialType === 'stone' && hasMax12InstallmentAndLower10) ||
                (financialType === 'stone' && hasOver12InstallmentAndLower10)
              }
            >
              {isStone ? 'Gerar pedido' : 'Gerar financeiro'}
            </SaveButton>
          </Col>

          {stoneDevice && (
            <Col>
              <Popover content={`Dispositivo: ${stoneDevice.serialNumber}`}>
                <div>
                  <ActionIcon
                    style={{ height: 32 }}
                    onClick={() => setStoneDevice(undefined)}
                  >
                    <img
                      src="/images/device-stone.png"
                      alt="Device Stone"
                      width={24}
                    />
                  </ActionIcon>
                </div>
              </Popover>
            </Col>
          )}
        </Row>

        <StoneDevicesSelectModal
          open={isStone && !stoneDevice}
          setDevice={setStoneDevice}
          device={stoneDevice}
          stoneAccount={stoneAccount.data}
          onClose={() => {
            if (isStone) {
              setFinancialType(FinancialType.Default);
              setStoneDevice(undefined);
            }
          }}
        />

        <div
          style={{ position: 'relative', borderRadius: 12, overflow: 'hidden' }}
        >
          <StoneOrderProcessInfo
            watch={{
              originType: StoneOrderOriginType.WORK_ORDER,
              companyId: workorder?.company.id,
              originId: workorder?.id,
            }}
            originCode={getWorkorderComplementNumber(
              workorder?.number,
              complement?.number,
            )}
            onFinish={(type) => {
              if (type === 'order') {
                setTimeout(() => {
                  queryChanel({
                    invalidateQueries: [
                      ['accountReceivable/financialSummary', workorder?.id],
                      ['accountReceivable/financialPending', workorder?.id],
                    ],
                  });
                }, 2000);
              }

              setStoneDevice(undefined);
              setFinancialType(FinancialType.Default);
              stoneModalAction.close();
            }}
          />

          <Table
            dataSource={[formValues]}
            columns={columns}
            pagination={false}
            scroll={{ x: getTableScrollX(columns) }}
            className="workorder-financial-config"
          />

          <Row gutter={[16, 16]} style={{ marginTop: 24, marginBottom: 24 }}>
            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                name="planAccountId"
                label="Plano de contas"
                rules={[ruleRequired]}
              >
                <PlanAccountsSelect
                  queryFilter={{
                    hierarchy: PlanAccountHierarchy.Analitical,
                    destination: PlanAccountDestination.Revenue,
                    active: true,
                  }}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem name="costCenterId" label="Centro de custo">
                <CostCenterInfiniteSelectField />
              </FloatingFormItem>
            </Col>

            {!isInstallmentFree && (
              <>
                <Col span={24} md={12} lg={6}>
                  <FloatingFormItem
                    name="paymentConditionId"
                    label="Condição de pagamento"
                  >
                    <PaymentConditionSelect
                      onSelectItem={setPaymentCondition}
                    />
                  </FloatingFormItem>
                </Col>
              </>
            )}

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                name="documentTypeId"
                label="Tipo de documento"
                rules={[ruleRequired]}
                getValueFromEvent={(value) => {
                  if (isStone) {
                    form.setFields([{ name: 'installmentQuantity', value: 1 }]);
                  }

                  return value;
                }}
              >
                <DocumentTypesInfiniteSelectField
                  queryFilter={{
                    active: true,
                    isStoneDocument: isStone,
                  }}
                  disabled={isCanceled}
                />
              </FloatingFormItem>
            </Col>

            {!isStone && (
              <Col
                span={24}
                md={12}
                lg={6}
                style={{ display: !isInstallmentFree ? 'none' : 'block' }}
              >
                <FloatingFormItem
                  name="dueDate"
                  label="Data de vencimento"
                  rules={[ruleRequired]}
                >
                  <InputDate />
                </FloatingFormItem>
              </Col>
            )}

            <Col
              span={24}
              md={6}
              lg={3}
              style={{ display: !isInstallmentFree ? 'none' : 'block' }}
            >
              <FloatingFormItem
                name="installmentQuantity"
                label="Qtd. Parcelas"
                rules={
                  isStone
                    ? [
                        ruleRequired,
                        { type: 'number', min: 1, message: 'Mínimo 1' },
                        {
                          type: 'number',
                          max: 18,
                          message: 'Limite de 18 parcelas',
                        },
                      ]
                    : [
                        ruleRequired,
                        { type: 'number', min: 1, message: 'Mínimo 1' },
                      ]
                }
                initialValue={1}
              >
                <InputDecimal
                  style={{ width: '100%' }}
                  fractions={0}
                  max={isStone ? 18 : undefined}
                  disabled={
                    isStone &&
                    documentTypeDetail.data?.paymentType ===
                      DocumentTypePaymentType.Debit
                  }
                />
              </FloatingFormItem>
            </Col>

            {!isStone && (
              <Col
                span={24}
                md={6}
                lg={3}
                style={{ display: !isInstallmentFree ? 'none' : 'block' }}
              >
                <FloatingFormItem
                  name="installmentsInterval"
                  label="Intervalo (dias)"
                  rules={[ruleRequired]}
                  initialValue={30}
                >
                  <InputDecimal
                    style={{ width: '100%' }}
                    min={0}
                    fractions={0}
                  />
                </FloatingFormItem>
              </Col>
            )}

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem label="Conta bancária" name="bankAccountId">
                <BankAccountInfiniteSelectField
                  allowClear
                  queryFilter={{ active: true }}
                  disabled={!!documentTypeDetail.data?.bankAccountId}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label="Número de autorização"
                name="authorizationNumber"
              >
                <Input />
              </FloatingFormItem>
            </Col>
          </Row>

          {hasMax12InstallmentAndLower10 && isStone && (
            <Alert
              style={{ marginTop: 24, marginBottom: 24 }}
              type="warning"
              description={
                <>
                  <strong>Existem parcelas com menos de R$0,10</strong>
                  <div>
                    Financeiro gerado com a Stone não permite criação de menos
                    ou igual de 12 parcelas com valor menor de R$0,10.
                  </div>
                </>
              }
            />
          )}

          {hasOver12InstallmentAndLower10 && isStone && (
            <Alert
              style={{ marginTop: 24, marginBottom: 24 }}
              type="warning"
              description={
                <>
                  <strong>Existem parcelas com menos de R$10,00</strong>
                  <div>
                    Financeiro gerado com a Stone não permite criação de mais de
                    12 parcelas com valor menor que R$10,00.
                  </div>
                </>
              }
            />
          )}

          <WorkOrderFinancialInstallments
            paymentCondition={paymentCondition}
            installments={installments}
            setInstallments={setInstallments}
            isStone={isStone}
            documentType={documentTypeDetail.data}
          />
        </div>
      </Form>

      <StoneOrderCreateModal
        installments={getInstallmentOrderStone()}
        opened={stoneModalOpened}
        onClose={stoneModalAction.close}
        device={stoneDevice}
        data={{
          paymentType: documentTypeDetail.data?.paymentType,
          displayName: `Orçamento - ${getWorkorderComplementNumber(
            workorder?.number,
            complement?.number,
          )}`.slice(0, 17),
          id: workorder?.id as number,
          originType: StoneOrderOriginType.WORK_ORDER,
          companyId: workorder?.company.id,
          stoneAccountId: stoneAccount.data?.id,
          customerId: getCustomerId,
          insurerId: getInsurerId,
          planAccountId: formValues?.planAccountId,
          documentTypeId: formValues?.documentTypeId,
          items: [
            {
              amount: Number(
                installments
                  .reduce((acc, item) => acc + (item.installmentValue || 0), 0)
                  .toFixed(2),
              ),

              description: `Orçamento - ${String(workorder?.number).padStart(
                6,
                '0',
              )}`,
              quantity: 1,
            },
          ],
        }}
      />
    </div>
  );
};

export default WorkOrderFinancialConfig;
